import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../data/config';
import SEO, { pageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Phone, Calendar, Clock, CreditCard, MapPin, 
  ExternalLink, Apple, Play, MessageCircle
} from 'lucide-react';

const ContactPage = () => {
  const { t, language } = useLanguage();

  const contactInfo = [
    {
      icon: Phone,
      label: t('contact.phone'),
      value: siteConfig.phone,
      link: siteConfig.phoneTel,
      primary: true,
    },
    {
      icon: Clock,
      label: t('contact.hours'),
      value: t('contact.hoursValue'),
    },
    {
      icon: CreditCard,
      label: t('contact.payment'),
      value: t('contact.paymentMethods'),
    },
    {
      icon: MapPin,
      label: t('contact.service'),
      value: t('contact.serviceArea'),
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      <SEO {...pageSEO.contact} />
      
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-primary/5 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <MessageCircle className="w-4 h-4" />
            {language === 'fr' ? 'Nous contacter' : 'Contact us'}
          </div>
          <h1 
            className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-foreground mb-6 tracking-tight"
            data-testid="contact-title"
          >
            {t('contact.title')}
          </h1>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card 
                  key={index}
                  className={`border-0 shadow-lg ${info.primary ? 'bg-primary text-white' : ''}`}
                  data-testid={`contact-info-${index}`}
                >
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-xl ${info.primary ? 'bg-white/20' : 'bg-primary/10'} flex items-center justify-center mb-4`}>
                      <Icon className={`w-6 h-6 ${info.primary ? 'text-white' : 'text-primary'}`} />
                    </div>
                    <div className={`text-sm ${info.primary ? 'text-white/80' : 'text-muted-foreground'} mb-1`}>
                      {info.label}
                    </div>
                    {info.link ? (
                      <a 
                        href={info.link}
                        className={`font-heading font-bold text-xl ${info.primary ? 'text-accent' : 'text-foreground'} hover:underline`}
                      >
                        {info.value}
                      </a>
                    ) : (
                      <div className={`font-semibold ${info.primary ? 'text-white' : 'text-foreground'}`}>
                        {info.value}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Booking Section */}
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <Calendar className="w-12 h-12 text-primary mb-4" />
                <h2 className="font-heading font-bold text-2xl text-foreground mb-4">
                  {t('contact.bookOnline')}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {t('contact.bookDesc')}
                </p>
                <Button
                  asChild
                  size="lg"
                  className="w-full font-bold h-14"
                  data-testid="contact-book-btn"
                >
                  <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">
                    {t('contact.bookOnline')}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-gradient-to-br from-gray-900 to-gray-800 text-white">
              <CardContent className="p-8">
                <div className="flex gap-2 mb-4">
                  <Apple className="w-8 h-8" />
                  <Play className="w-8 h-8" />
                </div>
                <h2 className="font-heading font-bold text-2xl mb-4">
                  {t('contact.appTitle')}
                </h2>
                <p className="text-gray-300 mb-6">
                  {t('contact.appDesc')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={siteConfig.appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-white text-foreground px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid="contact-appstore"
                  >
                    <Apple className="w-6 h-6" />
                    <span className="font-semibold text-sm">App Store</span>
                  </a>
                  <a
                    href={siteConfig.playStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-white text-foreground px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid="contact-playstore"
                  >
                    <Play className="w-6 h-6" />
                    <span className="font-semibold text-sm">Google Play</span>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Call CTA */}
      <section className="py-16 md:py-20 bg-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-accent-foreground mb-4">
            {language === 'fr' ? 'Le moyen le plus rapide: appelez-nous!' : 'The fastest way: call us!'}
          </h2>
          <p className="text-accent-foreground/80 mb-8">
            {language === 'fr'
              ? 'Un opérateur vous répondra immédiatement et vous enverra un taxi.'
              : 'An operator will answer immediately and send you a taxi.'}
          </p>
          <Button
            asChild
            size="lg"
            className="bg-foreground text-white hover:bg-foreground/90 font-bold text-xl h-16 px-10"
            data-testid="contact-call-btn"
          >
            <a href={siteConfig.phoneTel}>
              <Phone className="w-6 h-6 mr-3" />
              {siteConfig.phone}
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
